import { registerDevice, removeDevice } from '@notificationhub/client'

import { env } from '@/env'

interface Registration {
  userToken: string
  fcmToken: string
  claim: number
}

/**
 * Logout has no session left to mint a token with, so the one registration was
 * made with is kept here.
 *
 * `claim` and `revocation` exist because the FCM token cannot tell attempts
 * apart: a mount and a focus moments later normally carry the same one.
 */
let lastRegistration: Registration | null = null
let revocation = 0
let nextClaim = 0

/** Read before the caller's own awaits, so a logout during them is visible. */
export function currentRevocation(): number {
  return revocation
}

interface RegisterDeviceOptions {
  userToken: string
  fcmToken: string
  locale?: string
  revocationAtStart?: number
}

export async function registerNotificationHubDevice({
  userToken,
  fcmToken,
  locale,
  revocationAtStart,
}: RegisterDeviceOptions): Promise<boolean> {
  const baseUrl = env.REACT_APP_NOTIFICATIONHUB_URL
  if (!baseUrl || !userToken || !fcmToken) {
    return false
  }

  // Claimed before the request, so a logout mid-flight has something to delete.
  const previous = lastRegistration
  const claim = ++nextClaim
  const revokedBefore = revocationAtStart ?? revocation
  lastRegistration = { userToken, fcmToken, claim }

  const isStillOurs = () => lastRegistration?.claim === claim

  let ok: boolean
  try {
    ok = await registerDevice({
      baseUrl,
      userToken,
      token: fcmToken,
      platform: 'web',
      ...(locale ? { locale } : {}),
    })
  } catch {
    if (isStillOurs()) lastRegistration = previous
    return false
  }

  if (!ok) {
    if (isStillOurs()) lastRegistration = previous
    return false
  }

  /*
   * Being superseded is not being revoked. A device row is keyed by its browser
   * token, so an older attempt resolving after a newer one with the SAME token
   * must leave it alone — deleting it would remove what the newer attempt just
   * established. Only an orphan is ours to clean up.
   */
  const superseded = !isStillOurs()
  const revoked = revocation !== revokedBefore

  if (revoked || (superseded && lastRegistration?.fcmToken !== fcmToken)) {
    if (isStillOurs()) lastRegistration = null
    await deleteDevice(baseUrl, userToken, fcmToken)
    return false
  }

  return !superseded
}

function deleteDevice(
  baseUrl: string,
  userToken: string,
  fcmToken: string,
): Promise<boolean> {
  return removeDevice({ baseUrl, userToken, token: fcmToken })
}

export async function removeNotificationHubDevice(): Promise<boolean> {
  // Bumped even with nothing to remove, so a registration still in flight
  // learns its session ended.
  revocation += 1

  const baseUrl = env.REACT_APP_NOTIFICATIONHUB_URL
  if (!baseUrl || !lastRegistration) {
    return false
  }

  const { userToken, fcmToken } = lastRegistration
  lastRegistration = null

  return deleteDevice(baseUrl, userToken, fcmToken)
}
